import { ApiClient } from './api'
import {
  School,
  SchoolCreate,
  SchoolWithStats,
  SchoolInvitation,
  SchoolInvitationCreate,
  JoinSchoolRequest,
  SchoolRegistrationRequest,
  AuthToken,
  UserProfile
} from '@/types/school'

interface LoginRequest {
  username: string // email
  password: string
}

interface ListResponse<T> {
  items: T[]
  total: number
  page: number
  size: number
}

class SchoolApiService extends ApiClient {
  // Authentication endpoints
  async login(credentials: LoginRequest): Promise<AuthToken> {
    const formData = new FormData()
    formData.append('username', credentials.username)
    formData.append('password', credentials.password)
    
    const response = await this.post<AuthToken>('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })
    return response.data
  }

  async refreshToken(): Promise<AuthToken> {
    const response = await this.post<AuthToken>('/auth/refresh')
    return response.data
  }

  async joinSchool(request: JoinSchoolRequest): Promise<{ message: string }> {
    const response = await this.post<{ message: string }>('/auth/join-school', request)
    return response.data
  }

  // School management endpoints
  async registerSchool(schoolData: SchoolRegistrationRequest): Promise<School> {
    const response = await this.post<School>('/schools/register', schoolData)
    return response.data
  }

  async getCurrentSchool(): Promise<SchoolWithStats> {
    const response = await this.get<SchoolWithStats>('/schools/current')
    return response.data
  }

  async updateCurrentSchool(schoolData: Partial<SchoolCreate>): Promise<School> {
    const response = await this.put<School>('/schools/current', schoolData)
    return response.data
  }

  async listSchools(page: number = 1, size: number = 10): Promise<ListResponse<School>> {
    const response = await this.get<{ schools: School[]; total: number; page: number; size: number }>(`/schools/list?page=${page}&size=${size}`)
    return {
      items: response.data.schools,
      total: response.data.total,
      page: response.data.page,
      size: response.data.size
    }
  }

  // School invitation endpoints
  async createInvitation(invitation: SchoolInvitationCreate): Promise<SchoolInvitation> {
    const response = await this.post<SchoolInvitation>('/schools/invitations', invitation)
    return response.data
  }

  async listInvitations(page: number = 1, size: number = 10): Promise<ListResponse<SchoolInvitation>> {
    const response = await this.get<{ invitations: SchoolInvitation[]; total: number; page: number; size: number }>(`/schools/invitations?page=${page}&size=${size}`)
    return {
      items: response.data.invitations,
      total: response.data.total,
      page: response.data.page,
      size: response.data.size
    }
  }

  async getInvitation(invitationId: string): Promise<SchoolInvitation> {
    const response = await this.get<SchoolInvitation>(`/schools/invitations/${invitationId}`)
    return response.data
  }

  async updateInvitation(invitationId: string, updates: Partial<SchoolInvitationCreate>): Promise<SchoolInvitation> {
    const response = await this.put<SchoolInvitation>(`/schools/invitations/${invitationId}`, updates)
    return response.data
  }

  async deleteInvitation(invitationId: string): Promise<{ message: string }> {
    const response = await this.delete<{ message: string }>(`/schools/invitations/${invitationId}`)
    return response.data
  }

  async validateInvitation(invitationCode: string): Promise<{
    valid: boolean
    invitation?: {
      code: string
      role: string
      school_name: string
      expires_at?: string
    }
  }> {
    const response = await this.get<{
      valid: boolean
      invitation?: {
        code: string
        role: string
        school_name: string
        expires_at?: string
      }
    }>(`/schools/invitations/validate/${invitationCode}`)
    return response.data
  }

  // User profile endpoint
  async getCurrentUser(): Promise<UserProfile> {
    const response = await this.get<UserProfile>('/users/me')
    return response.data
  }
}

export const schoolApiService = new SchoolApiService()
export default schoolApiService